// Type-only import — erased at build time, so jsPDF stays out of the main
// bundle. The runtime module is pulled in via dynamic import() inside the
// generators, which Vite code-splits into its own chunk loaded only when a
// report is actually downloaded.
import type { jsPDF } from "jspdf";
import logoWhiteUrl from "../assets/neucode-logo-white.png";
import brainOnlyUrl from "../assets/neucode-brain.png";

/**
 * NeuSim — branded PDF reports (jsPDF, vector text + real logo).
 *
 * v3.1 redesign:
 *  - Dedicated cover page (identity-only for learner; KPIs + band-mix for cohort).
 *  - Real NeuCode Talent rectangular logo (white-text variant) in the dark
 *    header band on every page, embedded from src/assets/neucode-logo-white.png.
 *  - Colourful brain mark on the learner cover (src/assets/neucode-brain.png).
 *  - Learner page 2 opens with a 2-up Score / Band hero + per-decision
 *    sparkline + one-line story callout — pushed off the cover per Harish's
 *    direction.
 *  - Cohort cover keeps KPI tiles + band-mix tri-stripe (per Harish's call).
 *
 *   generateLearnerReportPdf — cover + performance hero + decisions + reflection + coaching.
 *   generateCohortReportPdf  — cover + insights + standings.
 */

// ─── Brand palette (RGB tuples for jsPDF) ─────────────────────────────────────
const BRAND = {
  bandDark:      [30, 27, 75]    as const, // #1e1b4b — dark indigo (header band)
  bandText:      [255, 255, 255] as const,
  bandSubtle:    [196, 181, 253] as const, // lavender ish
  accent:        [109, 40, 217]  as const, // #6d28d9 — violet (learner side rule, headings)
  accent2:       [8, 145, 178]   as const, // #0891b2 — cyan (cohort side rule)
  body:          [31, 41, 55]    as const,
  bodySoft:      [55, 65, 81]    as const,
  muted:         [107, 114, 128] as const,
  separator:     [229, 231, 235] as const,
  cardBg:        [250, 250, 253] as const,
  cardBorder:    [232, 230, 248] as const,
  cardBorderSoft:[229, 231, 235] as const,
  footerBg:      [31, 41, 55]    as const,
  footerText:    [209, 213, 219] as const,
  strong:        [16, 185, 129]  as const, // emerald
  strongTint:   [236, 253, 245] as const,
  strongBorder: [167, 243, 208] as const,
  strongText:   [6, 95, 70]     as const,
  developing:    [217, 119, 6]   as const, // amber-600
  developingTint:[254, 243, 199]as const,
  developingBorder:[252, 211, 77]as const,
  developingText:[146, 64, 14]  as const,
  needsWork:     [220, 38, 38]   as const, // red-600
  needsTint:    [254, 226, 226] as const,
  needsBorder:  [252, 165, 165] as const,
  needsText:    [153, 27, 27]   as const,
  cardAccent:    [237, 233, 254] as const, // light violet — avatar bg
} as const;

// ─── Layout constants (A4, units = mm) ────────────────────────────────────────
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 15;
const CONTENT_W = PAGE_W - 2 * MARGIN;
const BAND_H = 11;            // header band — slim so logo + tag have breathing room
const FOOTER_BAND_H = 8;
const CONTENT_TOP = BAND_H + 9;
const CONTENT_BOTTOM = PAGE_H - FOOTER_BAND_H - 3;

// Logo dimensions in mm for header band. 2020LOGOWHITE.png aspect ≈ ~4:1 wordmark
// with brain mark to its left. 38 × 7 reads cleanly at print scale.
const LOGO_W = 38;
const LOGO_H = 7;

function bandRgb(score: number): readonly [number, number, number] {
  if (score >= 8) return BRAND.strong;
  if (score >= 5) return BRAND.developing;
  return BRAND.needsWork;
}

function bandTintTriple(score: number): { bg: readonly [number, number, number]; border: readonly [number, number, number]; text: readonly [number, number, number]; label: string } {
  if (score >= 8) return { bg: BRAND.strongTint,    border: BRAND.strongBorder,    text: BRAND.strongText,    label: "Strong response" };
  if (score >= 5) return { bg: BRAND.developingTint, border: BRAND.developingBorder, text: BRAND.developingText, label: "Developing" };
  return                 { bg: BRAND.needsTint,      border: BRAND.needsBorder,      text: BRAND.needsText,      label: "Needs work" };
}

// ─── Asset loader: cache image data URLs across calls ─────────────────────────
let cachedLogoWhite: string | null = null;
let cachedBrainOnly: string | null = null;

async function loadImageDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to load image: ${url}`);
  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

async function getLogoWhite(): Promise<string> {
  if (cachedLogoWhite !== null) return cachedLogoWhite;
  cachedLogoWhite = await loadImageDataUrl(logoWhiteUrl);
  return cachedLogoWhite;
}

async function getBrainOnly(): Promise<string> {
  if (cachedBrainOnly !== null) return cachedBrainOnly;
  cachedBrainOnly = await loadImageDataUrl(brainOnlyUrl);
  return cachedBrainOnly;
}

// ─── Shared chrome ────────────────────────────────────────────────────────────
interface Chrome {
  docType: string;
  sideRule: readonly [number, number, number];
  logoWhite: string;
}

function drawHeaderBand(doc: jsPDF, c: Chrome): void {
  doc.setFillColor(...BRAND.bandDark);
  doc.rect(0, 0, PAGE_W, BAND_H, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...BRAND.bandSubtle);
  doc.text(spaced(`NEUSIM  ·  ${c.docType}`), MARGIN, BAND_H / 2 + 0.8);

  // Real rectangular NeuCode Talent logo, white text, top-right.
  doc.addImage(c.logoWhite, "PNG", PAGE_W - MARGIN - LOGO_W, (BAND_H - LOGO_H) / 2, LOGO_W, LOGO_H);

  doc.setTextColor(...BRAND.body);
  doc.setCharSpace(0);
}

function drawSideRule(doc: jsPDF, c: Chrome, fromY: number, toY: number): void {
  doc.setFillColor(...c.sideRule);
  doc.rect(0, fromY, 4, toY - fromY, "F");
}

function drawFooters(doc: jsPDF): void {
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(...BRAND.footerBg);
    doc.rect(0, PAGE_H - FOOTER_BAND_H, PAGE_W, FOOTER_BAND_H, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...BRAND.footerText);
    doc.text(
      `© 2026 NeuCode Talent Academy LLP  ·  www.neucodetalent.com`,
      MARGIN, PAGE_H - FOOTER_BAND_H / 2 + 0.8,
    );
    doc.text(`Page ${p} of ${totalPages}`, PAGE_W - MARGIN, PAGE_H - FOOTER_BAND_H / 2 + 0.8, { align: "right" });
  }
}

function ensureRoom(doc: jsPDF, y: number, needed: number, c: Chrome): number {
  if (y + needed > CONTENT_BOTTOM) {
    doc.addPage();
    drawHeaderBand(doc, c);
    return CONTENT_TOP;
  }
  return y;
}

function drawHeading(doc: jsPDF, title: string, color: readonly [number, number, number], y: number): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...color);
  doc.text(spaced(title), MARGIN, y);
  doc.setDrawColor(...BRAND.separator);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y + 1.5, PAGE_W - MARGIN, y + 1.5);
  return y + 7;
}

function drawRichText(doc: jsPDF, raw: string, yIn: number, c: Chrome): number {
  let y = yIn;
  const lines = raw.replace(/\r/g, "").split("\n");
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) { y += 2.5; continue; }

    const headingMatch = line.match(/^#{1,6}\s+(.*)$/) || line.match(/^\*\*(.+)\*\*:?$/);
    if (headingMatch !== null) {
      const text = (headingMatch[1] ?? "").replace(/\*\*/g, "").replace(/:$/, "");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...BRAND.body);
      const wrapped = doc.splitTextToSize(text, CONTENT_W);
      y = ensureRoom(doc, y, wrapped.length * 5.2 + 3, c);
      doc.text(wrapped, MARGIN, y);
      y += wrapped.length * 5.2 + 2.5;
      continue;
    }

    const bulletMatch = line.match(/^[-*•]\s+(.*)$/);
    const isBullet = bulletMatch !== null;
    const text = (isBullet ? bulletMatch[1] ?? "" : line).replace(/\*\*/g, "");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.bodySoft);
    const prefix = isBullet ? "•  " : "";
    const wrapped = doc.splitTextToSize(prefix + text, CONTENT_W - (isBullet ? 3 : 0));
    y = ensureRoom(doc, y, wrapped.length * 4.8 + 1.5, c);
    doc.text(wrapped, MARGIN + (isBullet ? 1 : 0), y);
    y += wrapped.length * 4.8 + 1.5;
  }
  return y + 2;
}

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) return "·";
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

function fmtLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function fmtShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * jsPDF's built-in Helvetica is WinANSI-only and silently substitutes
 * unsupported glyphs (₹ → ¹, smart quotes → ?, em-dash etc.) with
 * fallback bytes whose widths don't match the original. That mismatch
 * causes splitTextToSize to wrap to the WRONG width and lines overflow
 * the page — Harish's June 17 D3 report cut off "alloca[ted ₹14.5...]"
 * past the right margin. Normalising the text to plain ASCII before
 * we wrap fixes both the width and the readability.
 */
function sanitizeForPdf(text: string): string {
  return text
    .replace(/₹/g, "Rs ")              // ₹ rupee sign
    .replace(/[‘’]/g, "'")        // smart single quotes
    .replace(/[“”]/g, '"')        // smart double quotes
    .replace(/[–—]/g, "-")        // en/em dash
    .replace(/…/g, "...")              // ellipsis
    .replace(/ /g, " ")                // non-breaking space
    .replace(/[​-‏﻿]/g, "")  // zero-width / BOM
    .replace(/[^\x00-\x7F]/g, "?");         // everything else → ?
}

// ─── Coaching debrief parser + section colour palette ─────────────────────────
type CoachingSectionKind = "regular" | "pullquote" | "numbered" | "signature";

interface CoachingSection {
  title: string;
  body: string;
  kind: CoachingSectionKind;
  rule: readonly [number, number, number];
  accent: readonly [number, number, number];
  tint: readonly [number, number, number];
}

const SECTION_PALETTE: ReadonlyArray<{ rule: readonly [number, number, number]; accent: readonly [number, number, number]; tint: readonly [number, number, number] }> = [
  { rule: [16, 185, 129], accent: [6, 95, 70],     tint: [236, 253, 245] }, // emerald
  { rule: [217, 119, 6],  accent: [146, 64, 14],   tint: [254, 243, 199] }, // amber
  { rule: [109, 40, 217], accent: [109, 40, 217],  tint: [250, 245, 255] }, // violet
  { rule: [8, 145, 178],  accent: [21, 94, 117],   tint: [207, 250, 254] }, // cyan
  { rule: [107, 114, 128], accent: [55, 65, 81],   tint: [249, 250, 251] }, // muted (signature)
];

function classifySection(title: string, index: number, isLast: boolean): CoachingSectionKind {
  const t = title.toLowerCase();
  if (isLast && /(coach|note|closing|finally|signature)/.test(t)) return "signature";
  if (/(deeper pattern|core pattern|key insight|theme|pattern across)/.test(t)) return "pullquote";
  if (/(practice next|what to do|action item|next step|do next)/.test(t)) return "numbered";
  return "regular";
}

function parseCoachingSections(raw: string): CoachingSection[] {
  const out: Array<{ title: string; body: string[] }> = [];
  let current: { title: string; body: string[] } | null = null;
  const lines = raw.replace(/\r/g, "").split("\n");
  for (const line of lines) {
    const hMatch = line.match(/^#{1,6}\s+(.*)$/) || line.match(/^\*\*([^*]+)\*\*:?\s*$/);
    if (hMatch !== null) {
      if (current !== null) out.push(current);
      current = { title: (hMatch[1] ?? "").replace(/[:*]+$/, "").trim(), body: [] };
      continue;
    }
    if (current === null) current = { title: "", body: [] };
    current.body.push(line);
  }
  if (current !== null) out.push(current);

  // Drop empty pre-heading sections.
  const cleaned = out.filter((s) => s.title !== "" || s.body.join("").trim() !== "");

  // Assign colour + kind. Palette rotates; numbered/pullquote/signature override.
  return cleaned.map((s, i) => {
    const kind = classifySection(s.title, i, i === cleaned.length - 1);
    const paletteIdx =
      kind === "signature" ? 4 :
      kind === "pullquote" ? 2 :
      kind === "numbered"  ? 3 :
      i % SECTION_PALETTE.length;
    const p = SECTION_PALETTE[paletteIdx]!;
    return { title: s.title, body: s.body.join("\n").trim(), kind, rule: p.rule, accent: p.accent, tint: p.tint };
  });
}

/**
 * Detect numbered items in the body. Matches:
 *   "1. **Title.** Body of item"
 *   "1. Body of item"
 *   "1) Body of item"
 * Returns the list of items + the body text outside the numbered block.
 */
function parseNumberedItems(body: string): { intro: string; items: Array<{ title: string; body: string }>; outro: string } {
  const lines = body.split("\n");
  const itemStart = /^\s*(\d+)[.)]\s+(.*)$/;
  const items: Array<{ title: string; body: string }> = [];
  const intro: string[] = [];
  const outro: string[] = [];
  let phase: "intro" | "items" | "outro" = "intro";
  let cur: { title: string; body: string[] } | null = null;
  for (const raw of lines) {
    const m = raw.match(itemStart);
    if (m !== null) {
      if (cur !== null) items.push({ title: cur.title, body: cur.body.join(" ").trim() });
      phase = "items";
      const rest = m[2] ?? "";
      const titleMatch = rest.match(/^\*\*([^*]+)\*\*\.?\s*(.*)$/);
      cur = titleMatch !== null
        ? { title: (titleMatch[1] ?? "").trim(), body: [(titleMatch[2] ?? "").trim()] }
        : { title: "", body: [rest.trim()] };
      continue;
    }
    if (phase === "intro") intro.push(raw);
    else if (phase === "items") {
      if (raw.trim() === "" && cur !== null) { items.push({ title: cur.title, body: cur.body.join(" ").trim() }); cur = null; phase = "outro"; continue; }
      if (cur !== null) cur.body.push(raw.trim());
      else outro.push(raw);
    } else outro.push(raw);
  }
  if (cur !== null) items.push({ title: cur.title, body: cur.body.join(" ").trim() });
  return { intro: intro.join("\n").trim(), items, outro: outro.join("\n").trim() };
}

// ─── Readiness-verdict closing-call parser ────────────────────────────────────
interface ReadinessCall {
  band: "STRONG" | "DEVELOPING" | "NEEDS-WORK";
  n: number | null;
  verdict: string;  // The short closing pill (one sentence)
  body: string;     // The descriptive body for this band (multi-sentence)
}

/**
 * Find ALL band markers in the entire text (paragraph boundaries are
 * unreliable — Harish's June 17 cohort report came back with all three
 * bands inline on one line, which made the paragraph-split approach in
 * v3.3/v3.4 pick up only the LAST marker and render everything else as
 * one giant blob).
 *
 * Strategy: scan the whole string for marker positions. Each marker has
 * a "segment" = text from its end to the next marker's start. Group
 * segments by band. For each band, the LAST occurrence is treated as
 * the closing pill, earlier occurrences are body. Always orders the
 * output STRONG -> DEVELOPING -> NEEDS-WORK regardless of input order.
 */
function extractReadinessCalls(text: string): { body: string; calls: ReadinessCall[] } {
  const cleaned = text.replace(/\r/g, "").trim();
  if (cleaned === "") return { body: "", calls: [] };

  const bandWord = "(?:STRONG|DEVELOPING|NEEDS[-\\s]?WORK)";
  const markerRe = new RegExp(`(${bandWord})\\s*(?:\\(n=(\\d+)\\))?\\s*[:\\-]\\s*`, "gi");

  interface Segment { band: "STRONG" | "DEVELOPING" | "NEEDS-WORK"; n: number | null; text: string }
  const all: Segment[] = [];

  const markers: Array<{ start: number; end: number; band: string; n: number | null }> = [];
  let m: RegExpExecArray | null;
  while ((m = markerRe.exec(cleaned)) !== null) {
    markers.push({
      start: m.index,
      end: m.index + m[0].length,
      band: (m[1] ?? "").toUpperCase().replace(/\s+/g, "-"),
      n: m[2] !== undefined ? parseInt(m[2], 10) : null,
    });
  }
  if (markers.length === 0) return { body: cleaned, calls: [] };

  for (let i = 0; i < markers.length; i++) {
    const mk = markers[i]!;
    const next = i + 1 < markers.length ? markers[i + 1]!.start : cleaned.length;
    const segText = cleaned.slice(mk.end, next).trim().replace(/\s+/g, " ");
    const key: Segment["band"] =
      mk.band.startsWith("STRONG") ? "STRONG" :
      mk.band.startsWith("DEVELOP") ? "DEVELOPING" : "NEEDS-WORK";
    all.push({ band: key, n: mk.n, text: segText });
  }

  // Group by band, preserving order of first occurrence.
  const byBand = new Map<Segment["band"], Segment[]>();
  for (const seg of all) {
    if (!byBand.has(seg.band)) byBand.set(seg.band, []);
    byBand.get(seg.band)!.push(seg);
  }

  const order: Segment["band"][] = ["STRONG", "DEVELOPING", "NEEDS-WORK"];
  const calls: ReadinessCall[] = [];
  for (const key of order) {
    const segs = byBand.get(key);
    if (!segs || segs.length === 0) continue;
    if (segs.length === 1) {
      calls.push({ band: key, n: segs[0]!.n, verdict: segs[0]!.text, body: "" });
    } else {
      const pill = segs[segs.length - 1]!;
      const bodyText = segs.slice(0, -1).map((s) => s.text).join(" ").trim();
      calls.push({ band: key, n: pill.n ?? segs[0]!.n, verdict: pill.text, body: bodyText });
    }
  }

  return { body: "", calls };
}

/**
 * Break a prose paragraph into sentence-shaped bullets. Used to make the
 * readiness verdict body scannable instead of a wall of text.
 * Keeps sentence terminators on the bullet, joins very short fragments
 * (<25 chars) back to the previous bullet so abbreviations like "D2"
 * don't create stub bullets.
 */
function paragraphToBullets(text: string): string[] {
  if (text.trim() === "") return [];
  // Split on sentence-end punctuation followed by a space + capital/quote/digit.
  const raw = text.replace(/\s+/g, " ").trim();
  const parts: string[] = [];
  const re = /([^.!?]+[.!?]+)(?:\s+(?=["“]?[A-Z0-9]))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    parts.push(m[1]!.trim());
    last = m.index + m[0].length;
  }
  const tail = raw.slice(last).trim();
  if (tail !== "") parts.push(tail);

  // Merge tiny fragments forward.
  const merged: string[] = [];
  for (const p of parts) {
    if (merged.length > 0 && p.length < 25) merged[merged.length - 1] = `${merged[merged.length - 1]} ${p}`;
    else merged.push(p);
  }
  return merged;
}

function readinessTone(band: ReadinessCall["band"]): { bg: readonly [number, number, number]; border: readonly [number, number, number]; text: readonly [number, number, number] } {
  if (band === "STRONG")     return { bg: BRAND.strongTint,     border: BRAND.strongBorder,     text: BRAND.strongText };
  if (band === "DEVELOPING") return { bg: BRAND.developingTint, border: BRAND.developingBorder, text: BRAND.developingText };
  return                       { bg: BRAND.needsTint,           border: BRAND.needsBorder,      text: BRAND.needsText };
}

// ─── Measurement helpers ──────────────────────────────────────────────────────
function measureLines(doc: jsPDF, text: string, width: number, fontSize: number): number {
  // Force normal helvetica so the measurement matches drawProseCard's render
  // font. Without this, whatever style was active leaks into the width
  // tables and the precomputed card height drifts from the actual rendered
  // height — visible as text spilling past the card border.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  return doc.splitTextToSize(text, width).length;
}

/** Measure the height of a "prose card" (uppercase label + body) in mm. */
function measureProseCard(doc: jsPDF, body: string, width: number): number {
  const lineH = 4.6; // mm per line at 10pt
  const padding = 5; // top/bottom + label gap
  const labelGap = 5;
  const innerW = width - 8;
  const n = measureLines(doc, body, innerW, 10);
  return padding + labelGap + n * lineH + padding;
}

/**
 * Render a coaching section's body as paragraphs + bullets. Strips any **bold**
 * inline markup, treats blank lines as paragraph breaks, and renders -/* bullets.
 * Used after the section heading has been drawn separately.
 */
function drawSectionBody(doc: jsPDF, raw: string, yIn: number, c: Chrome): number {
  let y = yIn;
  const lines = raw.replace(/\r/g, "").split("\n");
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line === "") { y += 1.5; continue; }
    // Skip any embedded heading marks — section heading is already drawn.
    if (/^#{1,6}\s/.test(line)) continue;
    const bulletMatch = line.match(/^[-*•]\s+(.*)$/);
    const isBullet = bulletMatch !== null;
    const text = (isBullet ? bulletMatch[1] ?? "" : line).replace(/\*\*/g, "");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.bodySoft);
    const prefix = isBullet ? "•  " : "";
    const wrapped = doc.splitTextToSize(prefix + text, CONTENT_W - (isBullet ? 3 : 0));
    y = ensureRoom(doc, y, wrapped.length * 4.8 + 1.5, c);
    doc.text(wrapped, MARGIN + (isBullet ? 1 : 0), y);
    y += wrapped.length * 4.8 + 1.5;
  }
  return y + 2;
}

function drawProseCard(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  body: string,
  accent: readonly [number, number, number],
): void {
  doc.setFillColor(...BRAND.cardBg);
  doc.setDrawColor(...BRAND.cardBorderSoft);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, 2.5, 2.5, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...accent);
  doc.text(spaced(label), x + 4, y + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.bodySoft);
  const lines = doc.splitTextToSize(body, w - 8);
  doc.text(lines, x + 4, y + 12);
}

/**
 * Insert spaces between characters of an uppercase eyebrow. We avoid
 * jsPDF's setCharSpace / { charSpace } option entirely because:
 *   1. The `options.charSpace || activeCharSpace` short-circuit in
 *      jsPDF's text() coerces `charSpace: 0` back to the active value,
 *      so you can't force a Tc 0 reset via options.
 *   2. setCharSpace() only flips an internal variable; it never emits
 *      Tc to the PDF content stream. So any non-zero Tc emitted by a
 *      previous draw persists until another text() call writes Tc again.
 * Net effect: charSpace state leaks across draws, and scenario / body
 * text inherits the eyebrow's tracking.
 *
 * Pre-spacing the string sidesteps all of that — Tc stays at 0 forever.
 */
function spaced(text: string, ratio: number = 1): string {
  const sep = " ".repeat(Math.max(1, Math.round(ratio)));
  return text.toUpperCase().split("").join(sep);
}

function drawEyebrow(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  opts?: { align?: "left" | "center" | "right"; charSpace?: number },
): void {
  // charSpace argument retained for source-call compatibility but ignored
  // — the spacing now comes from the inserted characters themselves.
  void opts?.charSpace;
  const out = spaced(text, 1);
  if (opts?.align !== undefined) doc.text(out, x, y, { align: opts.align });
  else doc.text(out, x, y);
}

/**
 * Draw a block of body text inside a panel that may need to span pages.
 * Returns the y after the last drawn chunk. The panel is drawn as a series
 * of contiguous rectangles — each page-spanning chunk gets the same fill
 * + left stripe, so the visual reads as one continuous panel.
 *
 * Use this for unbounded-length content (long open-text answers, multi-
 * paragraph readiness verdicts). Fixed-height callers should keep the
 * existing inline rect + text pattern.
 */
function drawPaginatedTextPanel(
  doc: jsPDF,
  x: number,
  yIn: number,
  w: number,
  text: string,
  c: Chrome,
  opts: {
    fontStyle: "normal" | "italic" | "bold";
    fontSize: number;
    lineH: number;
    color: readonly [number, number, number];
    bg?: readonly [number, number, number];
    border?: readonly [number, number, number];
    stripe?: readonly [number, number, number];
    padX?: number;
    padY?: number;
  },
): number {
  const padX = opts.padX ?? 4;
  const padY = opts.padY ?? 4.5;
  const stripeW = opts.stripe !== undefined ? 1.5 : 0;
  const textX = x + stripeW + padX;
  const textW = w - stripeW - 2 * padX;

  doc.setFont("helvetica", opts.fontStyle);
  doc.setFontSize(opts.fontSize);
  const allLines: string[] = doc.splitTextToSize(text, textW);
  if (allLines.length === 0) return yIn;

  let y = yIn;
  let idx = 0;
  while (idx < allLines.length) {
    const avail = Math.max(0, CONTENT_BOTTOM - y - 2 * padY);
    let linesFit = Math.max(1, Math.floor(avail / opts.lineH));
    // If almost nothing fits, jump to a new page.
    if (avail < opts.lineH * 2 && idx === 0 && allLines.length > 1) {
      doc.addPage();
      drawHeaderBand(doc, c);
      y = CONTENT_TOP;
      continue;
    }
    if (linesFit < 1) linesFit = 1;
    const chunk = allLines.slice(idx, idx + linesFit);
    const chunkH = chunk.length * opts.lineH + 2 * padY;

    if (opts.bg !== undefined) {
      doc.setFillColor(...opts.bg);
      if (opts.border !== undefined) {
        doc.setDrawColor(...opts.border);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, y, w, chunkH, 2, 2, "FD");
      } else {
        doc.rect(x, y, w, chunkH, "F");
      }
    }
    if (opts.stripe !== undefined) {
      doc.setFillColor(...opts.stripe);
      doc.rect(x, y, 1.5, chunkH, "F");
    }
    doc.setFont("helvetica", opts.fontStyle);
    doc.setFontSize(opts.fontSize);
    doc.setTextColor(...opts.color);
    doc.text(chunk, textX, y + padY + 3.5);

    idx += linesFit;
    y += chunkH;
    if (idx < allLines.length) {
      doc.addPage();
      drawHeaderBand(doc, c);
      y = CONTENT_TOP;
    }
  }
  return y;
}

// ─── Learner report ───────────────────────────────────────────────────────────
export interface LearnerReportData {
  sim: { title: string; competencyName: string | null };
  participant: { fullName: string; email?: string };
  responses: Array<{
    sequenceNo: number;
    title: string | null;
    scenarioText: string;
    questionType: string;
    answerText: string;
    score: number;
    band: string | null;
    feedback: string | null;
  }>;
  reflection: string | null;
  coaching: string | null;
  status: string | null;
  totals: { decisions: number; totalScore: number; maxPossible: number; avg: number; band: string | null };
}

export async function generateLearnerReportPdf(data: LearnerReportData): Promise<Blob> {
  const { jsPDF: JsPDFCtor } = await import("jspdf");
  const doc = new JsPDFCtor({ unit: "mm", format: "a4" });
  const [logoWhite, brainOnly] = await Promise.all([getLogoWhite(), getBrainOnly()]);
  const c: Chrome = { docType: "Simulation Report", sideRule: BRAND.accent, logoWhite };

  // ═══════════════════════════════ COVER PAGE ═══════════════════════════════
  drawHeaderBand(doc, c);
  drawSideRule(doc, c, BAND_H, PAGE_H - FOOTER_BAND_H);

  const COVER_LEFT = MARGIN + 10;
  let y = 45;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(spaced("SIMULATION"), COVER_LEFT, y);
  y += 9;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...BRAND.bandDark);
  const titleLines = doc.splitTextToSize(data.sim.title !== "" ? data.sim.title : "Simulation", CONTENT_W - 14);
  doc.text(titleLines, COVER_LEFT, y);
  y += titleLines.length * 10 + 1;

  if (data.sim.competencyName !== null && data.sim.competencyName !== "") {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...BRAND.muted);
    doc.text(data.sim.competencyName, COVER_LEFT, y);
    y += 8;
  }

  // Identity card — centred horizontally, sits in upper-mid of cover
  const cardX = COVER_LEFT;
  const cardW = CONTENT_W - 14;
  const cardY = 128;
  const cardH = 34;
  doc.setFillColor(...BRAND.cardBg);
  doc.setDrawColor(...BRAND.cardBorder);
  doc.setLineWidth(0.3);
  doc.roundedRect(cardX, cardY, cardW, cardH, 3, 3, "FD");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(spaced("PREPARED FOR"), cardX + 8, cardY + 8);

  // Avatar circle
  const avatarX = cardX + 12;
  const avatarY = cardY + 21;
  const avatarR = 6;
  doc.setFillColor(...BRAND.cardAccent);
  doc.circle(avatarX, avatarY, avatarR, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.accent);
  doc.text(getInitials(data.participant.fullName), avatarX, avatarY + 1.2, { align: "center" });

  // Name + (email)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...BRAND.body);
  doc.text(data.participant.fullName, avatarX + 10, cardY + 19.5);
  if (data.participant.email !== undefined && data.participant.email !== "") {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    doc.text(data.participant.email, avatarX + 10, cardY + 25);
  }

  // Centred brain mark watermark
  const brainSize = 50;
  doc.addImage(brainOnly, "PNG", PAGE_W / 2 - brainSize / 2, 195, brainSize, brainSize);

  // Date + bottom separator
  doc.setDrawColor(...BRAND.separator);
  doc.setLineWidth(0.2);
  doc.line(COVER_LEFT, 268, PAGE_W - COVER_LEFT, 268);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(`Generated ${fmtLongDate(new Date().toISOString())}`, COVER_LEFT, 274);
  doc.text("Personalised report", PAGE_W - COVER_LEFT, 274, { align: "right" });

  // ═════════════════════════ PAGE 2 — Performance hero ═════════════════════════
  doc.addPage();
  drawHeaderBand(doc, c);
  y = CONTENT_TOP;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(spaced("AT A GLANCE"), MARGIN, y);
  y += 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BRAND.bandDark);
  doc.text("Your performance", MARGIN, y);
  y += 10;

  // 2-up hero cards
  const heroCardH = 28;
  const halfW = (CONTENT_W - 5) / 2;

  // ── Score card ──
  doc.setFillColor(...BRAND.cardBg);
  doc.setDrawColor(...BRAND.cardBorderSoft);
  doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, y, halfW, heroCardH, 2.5, 2.5, "FD");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(spaced("FINAL SCORE"), MARGIN + 5, y + 6.5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...BRAND.bandDark);
  const scoreStr = String(data.totals.totalScore);
  doc.text(scoreStr, MARGIN + 5, y + 18);
  const sw = doc.getTextWidth(scoreStr);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.muted);
  doc.text(` / ${data.totals.maxPossible}`, MARGIN + 5 + sw, y + 17.5);
  doc.setFontSize(9);
  doc.text(
    `${data.totals.decisions} decision${data.totals.decisions === 1 ? "" : "s"}  ·  avg ${(data.totals.avg ?? 0).toFixed(1)} / 10`,
    MARGIN + 5, y + 24,
  );

  // ── Band card ──
  const bandX = MARGIN + halfW + 5;
  const bandTone = bandTintTriple(data.totals.avg ?? 0);
  const bandLabel = (data.totals.band !== null && data.totals.band !== "") ? data.totals.band : bandTone.label;
  doc.setFillColor(...bandTone.bg);
  doc.setDrawColor(...bandTone.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(bandX, y, halfW, heroCardH, 2.5, 2.5, "FD");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...bandTone.text);
  doc.text(spaced("BAND"), bandX + 5, y + 6.5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(bandLabel, bandX + 5, y + 17);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    data.status === "timed_out"
      ? "Attempt ended on the timer"
      : (data.totals.avg ?? 0) >= 8 ? "Top tier — ready for senior client work"
      : (data.totals.avg ?? 0) >= 5 ? "Solid foundation with specific gaps"
      : "Foundational gaps — coaching recommended",
    bandX + 5, y + 23,
  );

  y += heroCardH + 10;

  // ── Sparkline: score per decision ──
  if (data.responses.length > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(spaced("SCORE PER DECISION"), MARGIN, y);
    y += 4;

    const sparklineH = 28;
    const gap = 1.5;
    const barW = (CONTENT_W - gap * (data.responses.length - 1)) / data.responses.length;
    const baseY = y + sparklineH;
    data.responses.forEach((r, i) => {
      const score = Math.max(0, Math.min(10, r.score ?? 0));
      const h = (score / 10) * sparklineH;
      const [br, bg, bb] = bandRgb(score);
      doc.setFillColor(br, bg, bb);
      const x = MARGIN + i * (barW + gap);
      doc.rect(x, baseY - h, barW, h, "F");
    });
    y = baseY + 3;

    // Axis labels D1..Dn (only every-other if many)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...BRAND.muted);
    const stride = data.responses.length > 12 ? 2 : 1;
    data.responses.forEach((r, i) => {
      if (i % stride !== 0 && i !== data.responses.length - 1) return;
      const x = MARGIN + i * (barW + gap) + barW / 2;
      doc.text(`D${r.sequenceNo}`, x, y + 1.5, { align: "center" });
    });
    y += 7;

    // Legend
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const legendItems: Array<[readonly [number, number, number], string]> = [
      [BRAND.strong, "Strong (8-10)"],
      [BRAND.developing, "Developing (5-7)"],
      [BRAND.needsWork, "Needs work (0-4)"],
    ];
    let lx = MARGIN;
    legendItems.forEach(([col, label]) => {
      doc.setFillColor(...col);
      doc.rect(lx, y - 2.5, 3, 2.5, "F");
      doc.setTextColor(...BRAND.bodySoft);
      doc.text(label, lx + 4, y);
      lx += doc.getTextWidth(label) + 12;
    });
    y += 8;
  }

  // ── One-line "your story" callout ──
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.accent);
  doc.text(spaced("YOUR STORY IN ONE LINE"), MARGIN, y);
  y += 4;
  doc.setDrawColor(...BRAND.separator);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 5;

  const storyLine = buildLearnerStory(data);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.body);
  const storyLines = doc.splitTextToSize(storyLine, CONTENT_W);
  doc.text(storyLines, MARGIN, y);
  y += storyLines.length * 5.5 + 4;

  // ═════════════════ PAGE 3 — Coaching debrief (BLUF: take-away first) ═════════════════
  // Harish, 2026-06-17: "PERSONALISED COACHING DEBRIEF should come first
  // and then should come all the decision details". Moving from end-of-doc
  // to right after the at-a-glance so readers see the take-away before
  // the supporting per-decision evidence.
  if (data.coaching !== null && data.coaching !== "") {
    doc.addPage();
    drawHeaderBand(doc, c);
    y = CONTENT_TOP;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.accent);
    doc.text(spaced("PERSONALISED COACHING DEBRIEF"), MARGIN, y);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...BRAND.bandDark);
    doc.text("A read of your performance, written for you", MARGIN, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...BRAND.muted);
    doc.text(
      `Generated by Claude from your ${data.totals.decisions} decision${data.totals.decisions === 1 ? "" : "s"}, your reflection, and the per-decision evaluator notes.`,
      MARGIN, y,
    );
    y += 8;

    const sections = parseCoachingSections(sanitizeForPdf(data.coaching));
    sections.forEach((section, sIdx) => {
      const eyebrow = `SECTION ${String(sIdx + 1).padStart(2, "0")}`;
      const headBlockH = 13;
      y = ensureRoom(doc, y, headBlockH + 8, c);
      doc.setFillColor(...section.rule);
      doc.rect(MARGIN, y - 1, 1.2, headBlockH, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...section.accent);
      doc.text(spaced(eyebrow), MARGIN + 5, y + 2.5);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(section.title !== "" ? section.title : "Coaching", MARGIN + 5, y + 9);
      y += headBlockH + 5;

      if (section.kind === "pullquote") {
        const paras = section.body.split(/\n\n+/);
        const quoteRaw = (paras[0] ?? "").replace(/\s+/g, " ").trim();
        const restRaw = paras.slice(1).join("\n\n").trim();
        if (quoteRaw !== "") {
          // Set the SAME font used for rendering BEFORE measuring — jsPDF's
          // splitTextToSize uses the active font width tables, so a mismatch
          // between measure-font and render-font wraps to the wrong width.
          doc.setFont("helvetica", "italic");
          doc.setFontSize(11.5);
          const lines = doc.splitTextToSize(quoteRaw, CONTENT_W - 20);
          const cardH = lines.length * 5.2 + 12;
          y = ensureRoom(doc, y, cardH + 4, c);
          doc.setFillColor(...section.tint);
          doc.setDrawColor(...section.rule);
          doc.setLineWidth(0.4);
          doc.roundedRect(MARGIN, y, CONTENT_W, cardH, 3, 3, "FD");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(38);
          doc.setTextColor(...section.rule);
          doc.text('"', MARGIN + 4, y + 13);
          doc.setFont("helvetica", "italic");
          doc.setFontSize(11.5);
          doc.setTextColor(...BRAND.bandDark);
          doc.text(lines, MARGIN + 12, y + 8);
          y += cardH + 5;
        }
        if (restRaw !== "") {
          y = drawSectionBody(doc, restRaw, y, c);
        }
      } else if (section.kind === "numbered") {
        const parsed = parseNumberedItems(section.body);
        if (parsed.intro !== "") {
          y = drawSectionBody(doc, parsed.intro, y, c);
          y += 2;
        }
        parsed.items.forEach((item, iIdx) => {
          const titleText = item.title !== "" ? item.title : item.body.split(/[.!?]/)[0]?.slice(0, 80) ?? "";
          const bodyText = item.title !== "" ? item.body : item.body.split(/[.!?]\s*/).slice(1).join(". ").trim();
          // Measure title at 11pt bold (its render font)
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          const titleLines = doc.splitTextToSize(titleText, CONTENT_W - 18);
          // Measure body at 10pt normal (its render font)
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          const bodyLines = bodyText !== "" ? doc.splitTextToSize(bodyText, CONTENT_W - 18) : [];
          const itemH = titleLines.length * 5 + (bodyLines.length > 0 ? bodyLines.length * 4.4 + 2 : 0) + 4;
          y = ensureRoom(doc, y, itemH + 2, c);
          doc.setFillColor(...section.tint);
          doc.circle(MARGIN + 4, y + 4, 4, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(...section.accent);
          doc.text(String(iIdx + 1), MARGIN + 4, y + 5.5, { align: "center" });
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.setTextColor(...BRAND.bandDark);
          doc.text(titleLines, MARGIN + 12, y + 4);
          if (bodyLines.length > 0) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(...BRAND.bodySoft);
            doc.text(bodyLines, MARGIN + 12, y + 4 + titleLines.length * 5);
          }
          y += itemH + 2;
        });
        if (parsed.outro !== "") {
          y = drawSectionBody(doc, parsed.outro, y, c);
        }
      } else if (section.kind === "signature") {
        // Measure at the SAME italic font we'll render with.
        doc.setFont("helvetica", "italic");
        doc.setFontSize(11);
        const lines = doc.splitTextToSize(section.body, CONTENT_W - 18);
        const cardH = lines.length * 5.2 + 10;
        y = ensureRoom(doc, y, cardH + 4, c);
        doc.setFillColor(...BRAND.cardBg);
        doc.setDrawColor(...BRAND.cardBorderSoft);
        doc.setLineWidth(0.3);
        doc.roundedRect(MARGIN, y, CONTENT_W, cardH, 3, 3, "FD");
        doc.setFont("helvetica", "italic");
        doc.setFontSize(11);
        doc.setTextColor(...BRAND.bodySoft);
        doc.text(lines, MARGIN + 6, y + 7);
        y += cardH + 4;
      } else {
        y = drawSectionBody(doc, section.body, y, c);
      }
      y += 6;
    });
  }

  // ═════════════════ PAGE 4+ — Decision-by-decision (premium cards) ═════════════════
  doc.addPage();
  drawHeaderBand(doc, c);
  y = CONTENT_TOP;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.accent);
  drawEyebrow(doc, "DECISION-BY-DECISION", MARGIN, y, { charSpace: 1.4 });
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BRAND.bandDark);
  doc.text("How you handled each moment", MARGIN, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...BRAND.muted);
  doc.text(
    "Your response, the per-decision evaluator's read, and the band that earned the next branch.",
    MARGIN, y,
  );
  y += 8;

  if (data.responses.length === 0) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(10); doc.setTextColor(...BRAND.muted);
    doc.text("No decisions were recorded.", MARGIN, y); y += 6;
  }
  // Decision section: fixed-height marker pill at the top + full-width content
  // below. The previous 2-col card with a full-height left marker couldn't
  // paginate (a 40-line open-text response made the rect 190mm tall and ran
  // off the page). This layout lets the response/feedback panels paginate
  // freely via drawPaginatedTextPanel while the marker stays visually tight.
  const MARKER_H = 16;
  data.responses.forEach((r, i) => {
    // Sanitise non-ASCII before measuring — jsPDF's Helvetica width tables
    // don't match its glyph substitutions, so leaving raw ₹/smart-quotes
    // in the string causes splitTextToSize to mis-wrap.
    const scenarioText = sanitizeForPdf(r.scenarioText ?? "");
    const answerText = sanitizeForPdf(r.answerText !== "" ? r.answerText : "—");
    const feedbackText = sanitizeForPdf(r.feedback ?? "");
    const score = r.score ?? 0;
    const tone = bandTintTriple(score);
    const titleStr = r.title ?? `Decision ${r.sequenceNo ?? i + 1}`;

    // Reserve room for the header block (marker + title + first scenario line).
    // Body content paginates independently below.
    y = ensureRoom(doc, y, MARKER_H + 30, c);

    // ── Header strip: D# pill · band pill · score, full width ──
    doc.setFillColor(...tone.bg);
    doc.setDrawColor(...tone.border);
    doc.setLineWidth(0.4);
    doc.roundedRect(MARGIN, y, CONTENT_W, MARKER_H, 2.5, 2.5, "FD");

    // D# big numeral on the left
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...tone.text);
    const numStr = `D${r.sequenceNo ?? i + 1}`;
    doc.text(numStr, MARGIN + 5, y + 11);

    // Vertical separator
    doc.setDrawColor(...tone.border);
    doc.setLineWidth(0.4);
    doc.line(MARGIN + 22, y + 3, MARGIN + 22, y + MARKER_H - 3);

    // Band label (medium)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...tone.text);
    const bandTxt = (r.band ?? tone.label).toUpperCase();
    drawEyebrow(doc, bandTxt, MARGIN + 26, y + 7, { charSpace: 1.0 });
    // Score on the right
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...tone.text);
    doc.text(`${score} / 10`, PAGE_W - MARGIN - 5, y + 11, { align: "right" });
    y += MARKER_H + 4;

    // ── Decision title (full width) ──
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...BRAND.bandDark);
    const titleLines = doc.splitTextToSize(titleStr, CONTENT_W);
    y = ensureRoom(doc, y, titleLines.length * 5.5 + 2, c);
    doc.text(titleLines, MARGIN, y + 4);
    y += titleLines.length * 5.5 + 3;

    // ── Scenario (muted, full width) ──
    if (scenarioText !== "") {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...BRAND.muted);
      // Normalise embedded literal \n so wrapping doesn't break weirdly.
      // Wrap to CONTENT_W - 6 (not -2) so even a slight width-estimation
      // error from jsPDF doesn't push the line past the right margin.
      const scenarioLines = doc.splitTextToSize(scenarioText.replace(/\r/g, ""), CONTENT_W - 6);
      // Cap scenario at ~8 lines so it doesn't dominate; rest is in the sim.
      const cappedLines = scenarioLines.slice(0, 8);
      y = ensureRoom(doc, y, cappedLines.length * 4.2 + 3, c);
      doc.text(cappedLines, MARGIN, y);
      y += cappedLines.length * 4.2 + 4;
    }

    // ── YOUR RESPONSE — violet-stripe paginating quote panel ──
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...BRAND.muted);
    y = ensureRoom(doc, y, 8, c);
    drawEyebrow(doc, "YOUR RESPONSE", MARGIN, y);
    y += 3;
    y = drawPaginatedTextPanel(doc, MARGIN, y, CONTENT_W, answerText, c, {
      fontStyle: "italic", fontSize: 10, lineH: 4.6,
      color: BRAND.bandDark, bg: BRAND.cardBg, stripe: BRAND.accent,
      padX: 4, padY: 3.5,
    });
    y += 4;

    // ── COACH FEEDBACK — tinted paginating panel ──
    if (feedbackText !== "") {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...BRAND.muted);
      y = ensureRoom(doc, y, 8, c);
      drawEyebrow(doc, "COACH FEEDBACK", MARGIN, y);
      y += 3;
      y = drawPaginatedTextPanel(doc, MARGIN, y, CONTENT_W, feedbackText, c, {
        fontStyle: "normal", fontSize: 9.5, lineH: 4.4,
        color: BRAND.bodySoft, bg: [249, 250, 251], border: BRAND.cardBorderSoft,
        padX: 5, padY: 3.5,
      });
      y += 4;
    }

    // Subtle separator between decisions
    if (i < data.responses.length - 1) {
      y = ensureRoom(doc, y, 6, c);
      doc.setDrawColor(...BRAND.separator);
      doc.setLineWidth(0.3);
      doc.line(MARGIN + 30, y, PAGE_W - MARGIN - 30, y);
      y += 6;
    }
  });

  // ── Reflection ──
  if (data.reflection !== null && data.reflection !== "") {
    y = ensureRoom(doc, y, 28, c);
    y += 2;
    y = drawHeading(doc, "Your reflection", BRAND.accent, y);
    // CRITICAL: setFont BEFORE splitTextToSize. jsPDF's splitTextToSize uses
    // the CURRENT font's width tables to wrap; if we set italic AFTER, the
    // wrap measurement uses normal widths but the render uses italic widths
    // (which are wider in Helvetica), so lines overflow the card edge.
    // Bug was visible on D15 reflection 2026-06-17 — text spilling past
    // the right border of the gray box.
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.bodySoft);
    // Wrap at CONTENT_W - 12 (was -6) — the extra 6mm covers padding on
    // both sides of the inner text inside the card.
    const lines = doc.splitTextToSize(sanitizeForPdf(data.reflection), CONTENT_W - 12);
    const h = lines.length * 4.8 + 10;
    y = ensureRoom(doc, y, h, c);
    doc.setFillColor(...BRAND.cardBg);
    doc.setDrawColor(...BRAND.cardBorderSoft);
    doc.setLineWidth(0.3);
    doc.roundedRect(MARGIN, y, CONTENT_W, h, 2.5, 2.5, "FD");
    // Reapply font (roundedRect doesn't touch text state but be explicit).
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.bodySoft);
    doc.text(lines, MARGIN + 6, y + 6.5);
    y += h + 4;
  }

  // (Coaching debrief now renders BEFORE the decision-by-decision section
  // — see "PAGE 3 — Coaching debrief (BLUF: take-away first)" above.)

  drawFooters(doc);
  return doc.output("blob");
}

/** Single-sentence summary of the learner's performance for the page-2 callout. */
function buildLearnerStory(data: LearnerReportData): string {
  const avg = data.totals.avg ?? 0;
  const decisions = data.totals.decisions;
  const dips = data.responses.filter((r) => r.score < 5).length;
  const tops = data.responses.filter((r) => r.score >= 8).length;

  if (decisions === 0) return "No decisions recorded — see the coaching debrief for next steps.";

  if (avg >= 8) {
    if (dips === 0) return `Consistently high performance across all ${decisions} decisions — see the coaching debrief for what to take to the next engagement.`;
    return `Strong overall (${tops} of ${decisions} decisions at the top tier) with one or two dips worth reviewing in the decision cards below.`;
  }
  if (avg >= 5) {
    return `Solid foundation across ${decisions} decisions; ${tops > 0 ? `${tops} top-tier moves` : "no top-tier moves yet"} and ${dips} dip${dips === 1 ? "" : "s"} flagged — the coaching debrief calls out the patterns.`;
  }
  return `Foundational gaps surfaced across ${decisions} decisions (${dips} dip${dips === 1 ? "" : "s"}); the coaching debrief recommends specific practice areas before the next engagement.`;
}

// ─── Cohort report ────────────────────────────────────────────────────────────
export interface CohortReportLearner {
  label: string;
  band: string | null;
  totalScore: number | null;
  decisions: number;
  status: string;
}

export interface CohortInsightsPdf {
  strengths: string;
  development: string;
  themes: string;
  recommendations: string;
  readiness: string;
  decisionDeepDives: string;
}

export interface CohortReportData {
  simTitle: string;
  cohortName: string;
  clientName: string | null;
  projectName: string | null;
  assigned: number;
  started: number;
  completed: number;
  timedOut: number;
  avgScore: number | null;
  bestScore: number | null;
  bandMix: { strong: number; developing: number; needs: number };
  generatedAt: string;
  anonymous: boolean;
  leaderboard: CohortReportLearner[];
  insights: CohortInsightsPdf | null;
  insightsGeneratedAt: string | null;
}

export async function generateCohortReportPdf(data: CohortReportData): Promise<Blob> {
  const { jsPDF: JsPDFCtor } = await import("jspdf");
  const doc = new JsPDFCtor({ unit: "mm", format: "a4" });
  const logoWhite = await getLogoWhite();
  const c: Chrome = { docType: "Cohort Report", sideRule: BRAND.accent2, logoWhite };

  // ═══════════════════════════════ COVER PAGE ═══════════════════════════════
  drawHeaderBand(doc, c);
  drawSideRule(doc, c, BAND_H, PAGE_H - FOOTER_BAND_H);

  const COVER_LEFT = MARGIN + 10;
  let y = 45;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(spaced("COHORT ANALYTICS"), COVER_LEFT, y);
  y += 9;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...BRAND.bandDark);
  const titleLines = doc.splitTextToSize(data.simTitle, CONTENT_W - 14);
  doc.text(titleLines, COVER_LEFT, y);
  y += titleLines.length * 10 + 1;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.muted);
  const subtitle = [data.clientName, data.projectName, data.cohortName].filter((s): s is string => s !== null && s !== "").join("   ·   ");
  doc.text(subtitle, COVER_LEFT, y);
  y += 14;

  // 2x2 KPI tiles
  const kpiW = (CONTENT_W - 14 - 6) / 2;
  const kpiH = 22;
  const kpis: Array<{ label: string; value: string; sub?: string }> = [
    { label: "ASSIGNED",  value: String(data.assigned) },
    { label: "STARTED",   value: String(data.started),    sub: pct(data.started, data.assigned) },
    { label: "COMPLETED", value: String(data.completed),  sub: pct(data.completed, data.assigned) },
    { label: "AVG SCORE", value: data.avgScore !== null ? data.avgScore.toFixed(1) : "—", sub: data.completed > 0 ? `n=${data.completed}` : undefined },
  ];
  kpis.forEach((k, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const kx = COVER_LEFT + col * (kpiW + 6);
    const ky = y + row * (kpiH + 6);
    doc.setFillColor(...BRAND.cardBg);
    doc.setDrawColor(...BRAND.cardBorderSoft);
    doc.setLineWidth(0.3);
    doc.roundedRect(kx, ky, kpiW, kpiH, 2.5, 2.5, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...BRAND.muted);
    doc.text(spaced(k.label), kx + 5, ky + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(...BRAND.bandDark);
    doc.text(k.value, kx + 5, ky + 16);
    if (k.sub !== undefined) {
      const vw = doc.getTextWidth(k.value);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.muted);
      doc.text(k.sub, kx + 5 + vw + 3, ky + 15.5);
    }
  });
  y += kpiH * 2 + 6 + 14;

  // Band-mix tri-stripe
  const totalBand = data.bandMix.strong + data.bandMix.developing + data.bandMix.needs;
  if (totalBand > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(spaced("BAND MIX · COMPLETED LEARNERS"), COVER_LEFT, y);
    y += 4;
    const barH = 5;
    const sw_ = (data.bandMix.strong / totalBand) * (CONTENT_W - 14);
    const dw_ = (data.bandMix.developing / totalBand) * (CONTENT_W - 14);
    const nw_ = (data.bandMix.needs / totalBand) * (CONTENT_W - 14);
    // Background track
    doc.setFillColor(243, 244, 246);
    doc.roundedRect(COVER_LEFT, y, CONTENT_W - 14, barH, 1.5, 1.5, "F");
    // Filled segments — no rounding to keep segments adjacent
    let bx = COVER_LEFT;
    if (sw_ > 0) { doc.setFillColor(...BRAND.strong);     doc.rect(bx, y, sw_, barH, "F"); bx += sw_; }
    if (dw_ > 0) { doc.setFillColor(...BRAND.developing); doc.rect(bx, y, dw_, barH, "F"); bx += dw_; }
    if (nw_ > 0) { doc.setFillColor(...BRAND.needsWork);  doc.rect(bx, y, nw_, barH, "F"); }
    y += barH + 4;
    // Legend
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const legend: Array<[readonly [number, number, number], string, number]> = [
      [BRAND.strong,     "Strong",     data.bandMix.strong],
      [BRAND.developing, "Developing", data.bandMix.developing],
      [BRAND.needsWork,  "Needs work", data.bandMix.needs],
    ];
    let lx = COVER_LEFT;
    legend.forEach(([col, label, count]) => {
      doc.setFillColor(...col);
      doc.circle(lx + 1.5, y - 1.5, 1.5, "F");
      doc.setTextColor(...BRAND.bodySoft);
      const lbl = `${label} ${count}`;
      doc.text(lbl, lx + 4.5, y);
      lx += doc.getTextWidth(lbl) + 14;
    });
  }

  // Date + bottom separator
  doc.setDrawColor(...BRAND.separator);
  doc.setLineWidth(0.2);
  doc.line(COVER_LEFT, 268, PAGE_W - COVER_LEFT, 268);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(`Generated ${fmtLongDate(data.generatedAt)}`, COVER_LEFT, 274);
  doc.text(data.anonymous ? "Anonymous facilitator copy" : "Facilitator copy", PAGE_W - COVER_LEFT, 274, { align: "right" });

  // ═════════════════ PAGE 2 — Cohort insights (premium 2x2 + readiness pills) ═════════════════
  if (data.insights !== null) {
    doc.addPage();
    drawHeaderBand(doc, c);
    y = CONTENT_TOP;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.accent2);
    doc.text(spaced("COHORT INSIGHTS (AI ANALYSIS)"), MARGIN, y);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...BRAND.bandDark);
    doc.text(
      data.insightsGeneratedAt !== null
        ? `Generated ${fmtShortDate(data.insightsGeneratedAt)}`
        : "Cohort insights",
      MARGIN, y,
    );
    y += 8;

    // Sanitise all insights strings up-front — same WinANSI/glyph-width
    // mismatch as the learner report. Without this, ₹ and smart-quotes
    // in the readiness body cause wrap miscalculation.
    const insRaw = data.insights;
    const ins: CohortInsightsPdf = {
      strengths:         sanitizeForPdf(insRaw.strengths),
      development:       sanitizeForPdf(insRaw.development),
      themes:            sanitizeForPdf(insRaw.themes),
      recommendations:   sanitizeForPdf(insRaw.recommendations),
      readiness:         sanitizeForPdf(insRaw.readiness),
      decisionDeepDives: sanitizeForPdf(insRaw.decisionDeepDives),
    };

    // ── Readiness verdict — structured per-band cards ──
    // v3.4: was rendering as one giant wall of body text + 3 small pills.
    // Now: section title + one card per band (STRONG/DEVELOPING/NEEDS-WORK)
    // with a band-tinted header strip showing the verdict pill, and the
    // descriptive body below in a tinted panel with a band-coloured stripe.
    if (ins.readiness !== "") {
      const { body, calls } = extractReadinessCalls(ins.readiness);

      // Section title
      y = ensureRoom(doc, y, 14, c);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...BRAND.accent);
      doc.text(spaced("READINESS VERDICT"), MARGIN, y);
      y += 5;
      doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...BRAND.bandDark);
      doc.text("Where each band is ready to operate", MARGIN, y);
      y += 7;

      if (calls.length > 0) {
        // Per-band card. Fixed-width left column holds the band label so the
        // verdict pill aligns across all three bands. Body renders as bullets
        // (one per sentence) so the reader can scan instead of read prose.
        const LABEL_COL_W = 44;  // wide enough for "DEVELOPING (n=7)"
        calls.forEach((call) => {
          const tone = readinessTone(call.band);
          const label = call.n !== null ? `${call.band} (n=${call.n})` : call.band;

          // Pre-measure header (pill text wraps to fit)
          doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
          const verdictLines = doc.splitTextToSize(call.verdict, CONTENT_W - LABEL_COL_W - 8);
          const headerH = Math.max(12, verdictLines.length * 4.4 + 5);

          y = ensureRoom(doc, y, headerH + 14, c);

          // Header strip — band-tinted
          doc.setFillColor(...tone.bg);
          doc.setDrawColor(...tone.border);
          doc.setLineWidth(0.4);
          doc.roundedRect(MARGIN, y, CONTENT_W, headerH, 2.5, 2.5, "FD");
          // Band label (fixed column)
          doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...tone.text);
          doc.text(label, MARGIN + 4, y + headerH / 2 + 1);
          // Vertical separator at fixed column boundary
          doc.setDrawColor(...tone.border);
          doc.line(MARGIN + LABEL_COL_W, y + 2.5, MARGIN + LABEL_COL_W, y + headerH - 2.5);
          // Verdict pill text (right column, aligned)
          doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...tone.text);
          doc.text(verdictLines, MARGIN + LABEL_COL_W + 4, y + 5.5);
          y += headerH + 1;

          // Body — bulleted list with band-coloured left stripe
          if (call.body !== "") {
            const bullets = paragraphToBullets(call.body);
            if (bullets.length > 0) {
              const bulletW = CONTENT_W - 14;
              bullets.forEach((b) => {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(9.5);
                const wrapped = doc.splitTextToSize(b, bulletW);
                const lineH = 4.4;
                const blockH = wrapped.length * lineH + 4;
                y = ensureRoom(doc, y, blockH + 1, c);
                // Subtle background card
                doc.setFillColor(...BRAND.cardBg);
                doc.rect(MARGIN, y, CONTENT_W, blockH, "F");
                // Band-coloured left stripe
                doc.setFillColor(...tone.text);
                doc.rect(MARGIN, y, 1.5, blockH, "F");
                // Bullet glyph
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9.5);
                doc.setTextColor(...tone.text);
                doc.text("•", MARGIN + 5, y + 4.5);
                // Bullet text
                doc.setFont("helvetica", "normal");
                doc.setFontSize(9.5);
                doc.setTextColor(...BRAND.bodySoft);
                doc.text(wrapped, MARGIN + 9, y + 4.5);
                y += blockH + 1.5;
              });
            }
          }
          y += 5;
        });
      } else if (body !== "" || ins.readiness !== "") {
        // Fallback when paragraphs didn't follow the band-twice pattern.
        const bodyToRender = body !== "" ? body : ins.readiness;
        y = drawPaginatedTextPanel(doc, MARGIN, y, CONTENT_W, bodyToRender, c, {
          fontStyle: "normal", fontSize: 10, lineH: 4.8,
          color: BRAND.body,
          bg: [245, 243, 255], border: [196, 181, 253],
          padX: 5, padY: 4,
        });
      }
      y += 4;
    }

    // ── 2x2 grid: strengths / development on row 1, themes / recommendations on row 2 ──
    const colGap = 4;
    const colW = (CONTENT_W - colGap) / 2;
    const colX1 = MARGIN;
    const colX2 = MARGIN + colW + colGap;

    type CellSpec = { label: string; body: string; accent: readonly [number, number, number]; bullets?: boolean };
    const recsBullets = ins.recommendations.split("||").map((s) => s.trim()).filter((s) => s.length > 0);
    const recsRendered = recsBullets.map((s, i) => `${i + 1}. ${s}`).join("\n\n");

    const cells: CellSpec[] = [
      { label: "Strengths",            body: ins.strengths,   accent: BRAND.strongText },
      { label: "Development gaps",     body: ins.development, accent: BRAND.developingText },
      { label: "Cross-cohort themes",  body: ins.themes,      accent: BRAND.accent2 },
      { label: "Recommendations",      body: recsRendered,    accent: BRAND.accent, bullets: true },
    ];

    // Row 1: strengths + development gaps — match max height
    const cell0H = ins.strengths   !== "" ? measureProseCard(doc, ins.strengths,   colW) : 0;
    const cell1H = ins.development !== "" ? measureProseCard(doc, ins.development, colW) : 0;
    const row1H = Math.max(cell0H, cell1H);
    if (row1H > 0) {
      y = ensureRoom(doc, y, row1H + 6, c);
      if (cell0H > 0) drawProseCard(doc, colX1, y, colW, row1H, "Strengths",       ins.strengths,   BRAND.strongText);
      if (cell1H > 0) drawProseCard(doc, colX2, y, colW, row1H, "Development gaps", ins.development, BRAND.developingText);
      y += row1H + 5;
    }

    // Row 2: themes + recommendations
    const cell2H = ins.themes !== "" ? measureProseCard(doc, ins.themes, colW) : 0;
    const cell3H = recsRendered !== "" ? measureProseCard(doc, recsRendered, colW) : 0;
    const row2H = Math.max(cell2H, cell3H);
    if (row2H > 0) {
      y = ensureRoom(doc, y, row2H + 6, c);
      if (cell2H > 0) drawProseCard(doc, colX1, y, colW, row2H, "Cross-cohort themes", ins.themes,   BRAND.accent2);
      if (cell3H > 0) drawProseCard(doc, colX2, y, colW, row2H, "Recommendations",     recsRendered, BRAND.accent);
      y += row2H + 6;
    }

    // ── Decision deep-dives — full width ──
    if (ins.decisionDeepDives !== "") {
      const items = ins.decisionDeepDives.split("||").map((s) => s.trim()).filter((s) => s.length > 0);
      if (items.length > 0) {
        y = ensureRoom(doc, y, 14, c);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...BRAND.accent2);
        doc.text(spaced("DECISION DEEP-DIVES"), MARGIN, y);
        y += 3;
        doc.setDrawColor(...BRAND.separator);
        doc.setLineWidth(0.2);
        doc.line(MARGIN, y, PAGE_W - MARGIN, y);
        y += 4;
        items.forEach((item) => {
          // Paginate long deep-dives — Claude can return 200+ word paragraphs.
          y = drawPaginatedTextPanel(doc, MARGIN, y, CONTENT_W, item, c, {
            fontStyle: "normal", fontSize: 10, lineH: 4.8,
            color: BRAND.bodySoft, bg: BRAND.cardBg, border: BRAND.cardBorderSoft,
            stripe: BRAND.accent2, padX: 5, padY: 3.5,
          });
          y += 3;
        });
      }
    }
  }

  // ═════════════════ PAGE 3 — Standings ═════════════════
  if (data.leaderboard.length > 0) {
    doc.addPage();
    drawHeaderBand(doc, c);
    y = CONTENT_TOP;
    y = drawHeading(doc, data.anonymous ? "Standings (anonymous)" : "Standings", BRAND.accent2, y);

    // Column layout (right-aligned from page edge):
    //   score     at -2
    //   band pill at -16 (col is ~26mm wide; max content ~"NEEDS WORK")
    //   decisions at -46 (col is ~10mm wide for "14d")
    //   label     spans from MARGIN+9 to PAGE_W-MARGIN-56 (leaves room for the rest)
    const rowH = 7.5;
    const SCORE_X = PAGE_W - MARGIN - 2;
    const BAND_X = PAGE_W - MARGIN - 16;
    const DEC_X = PAGE_W - MARGIN - 46;
    const LABEL_MAX_W = (PAGE_W - MARGIN - 56) - (MARGIN + 9);
    const shortBand = (b: string): string => {
      const u = b.toUpperCase();
      if (u.startsWith("STRONG")) return "STRONG";
      if (u.startsWith("DEVELOP")) return "DEVELOPING";
      if (u.startsWith("NEEDS")) return "NEEDS WORK";
      return u;
    };
    data.leaderboard.forEach((row, i) => {
      y = ensureRoom(doc, y, rowH + 1, c);
      if (i % 2 === 0) {
        doc.setFillColor(248, 248, 253);
        doc.rect(MARGIN, y - 5, CONTENT_W, rowH, "F");
      }
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...BRAND.muted);
      doc.text(`${i + 1}`, MARGIN + 2, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...BRAND.body);
      const labelLine = doc.splitTextToSize(row.label, LABEL_MAX_W)[0] ?? row.label;
      doc.text(labelLine, MARGIN + 9, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...BRAND.muted);
      doc.text(`${row.decisions}d`, DEC_X, y, { align: "right" });
      const tone = row.totalScore !== null && row.decisions > 0
        ? bandTintTriple(row.totalScore / row.decisions)
        : { bg: BRAND.cardBg, border: BRAND.cardBorderSoft, text: BRAND.muted, label: row.status };
      const rawBand = row.band ?? (row.status === "completed" ? tone.label : row.status.replace("_", " "));
      // Shorten "Strong response" / "Developing" / "Needs work" so it fits
      // the band column without colliding with the decisions count.
      const bandStr = row.status === "completed" ? shortBand(rawBand) : rawBand;
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...tone.text);
      doc.text(bandStr, BAND_X, y, { align: "right" });
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...BRAND.accent2);
      doc.text(row.totalScore !== null ? `${row.totalScore}` : "—", SCORE_X, y, { align: "right" });
      y += rowH;
    });
  }

  drawFooters(doc);
  return doc.output("blob");
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function pct(num: number, den: number): string {
  if (den <= 0) return "—";
  return `${Math.round((num / den) * 100)}%`;
}

/** Trigger a browser download for a Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }, 1000);
}

/** Safe filename fragment from an arbitrary label. */
export function slugForFile(s: string): string {
  return s.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").slice(0, 50) || "report";
}

// ─── Dashboard PDFs (Phase G) ─────────────────────────────────────────────────
// One-pager-style branded summaries for the executive + manager dashboards.
// Reuses every helper above: chrome, header band, footers, paginated text,
// sanitizeForPdf, spaced, drawHeading. The dashboard PDF is light-themed
// (the dark glass UI doesn't survive print) but the brand chrome is identical
// to the learner + cohort reports.

const PERIOD_LABEL_PDF: Record<string, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "all": "All time",
};

// jsPDF's setFillColor / setTextColor are variadic at runtime but TypeScript
// loses the tuple narrowing when a `readonly [r,g,b]` flows through an
// intermediate (object property, conditional, etc.). These thin wrappers
// preserve the tuple shape at the boundary.
function fc(doc: jsPDF, c: readonly [number, number, number]): void { doc.setFillColor(c[0], c[1], c[2]); }
function tc(doc: jsPDF, c: readonly [number, number, number]): void { doc.setTextColor(c[0], c[1], c[2]); }

interface DashboardChromeOpts {
  clientName: string;
  view: "Executive" | "Manager";
  period: string;
}

async function drawDashboardCover(doc: jsPDF, c: Chrome, opts: DashboardChromeOpts): Promise<number> {
  drawHeaderBand(doc, c);
  drawSideRule(doc, c, BAND_H, PAGE_H - FOOTER_BAND_H);

  let y = CONTENT_TOP + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(spaced(`${opts.view.toUpperCase()} DASHBOARD`), MARGIN, y);
  y += 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...BRAND.bandDark);
  doc.text(sanitizeForPdf(opts.clientName), MARGIN, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.muted);
  const period = PERIOD_LABEL_PDF[opts.period] ?? opts.period;
  doc.text(`${period}  ·  Generated ${fmtLongDate(new Date().toISOString())}`, MARGIN, y);
  y += 10;

  return y;
}

// ─── Executive PDF ────────────────────────────────────────────────────────────
export interface ExecutiveDashboardPdfData {
  clientName: string;
  period: string;
  readiness: { strong: number; developing: number; needsWork: number; total: number };
  uplift: { firstScore: number | null; latestScore: number | null; delta: number | null; n: number };
  themes: Array<{ title: string; body: string; severity: "strong" | "developing" | "needs" }>;
  competencies: Array<{ name: string; avgScore: number | null; n: number }>;
  topPerformers: Array<{ rank: number; fullName: string; cohortName: string; avgScore: number | null; n: number }>;
  standoutCohort: { cohortName: string; avgScore: number | null; n: number } | null;
  concernCohort: { cohortName: string; avgScore: number | null; n: number } | null;
}

export interface DashboardPdfSections {
  readiness?: boolean;
  uplift?: boolean;
  themes?: boolean;
  competencies?: boolean;
  topPerformers?: boolean;
  highlights?: boolean;
  funnel?: boolean;
  actionQueues?: boolean;
  completionBySim?: boolean;
  timeToComplete?: boolean;
  dropOff?: boolean;
}

export async function generateExecutiveDashboardPdf(
  data: ExecutiveDashboardPdfData,
  sections: DashboardPdfSections,
): Promise<Blob> {
  const { jsPDF: JsPDFCtor } = await import("jspdf");
  const doc = new JsPDFCtor({ unit: "mm", format: "a4" });
  const logoWhite = await getLogoWhite();
  const c: Chrome = { docType: "Executive Dashboard", sideRule: BRAND.accent, logoWhite };

  let y = await drawDashboardCover(doc, c, {
    clientName: data.clientName, view: "Executive", period: data.period,
  });

  // Readiness mix
  if (sections.readiness !== false) {
    y = ensureRoom(doc, y, 28, c);
    y = drawHeading(doc, "Readiness mix", BRAND.accent, y);
    const total = data.readiness.total;
    const pct = (n: number): number => total > 0 ? Math.round((n / total) * 100) : 0;
    const readinessRows = [
      { label: "Strong",     count: data.readiness.strong,     pct: pct(data.readiness.strong),     color: BRAND.strong },
      { label: "Developing", count: data.readiness.developing, pct: pct(data.readiness.developing), color: BRAND.developing },
      { label: "Needs work", count: data.readiness.needsWork,  pct: pct(data.readiness.needsWork),  color: BRAND.needsWork },
    ] as const;
    readinessRows.forEach((row) => {
      y = ensureRoom(doc, y, 10, c);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.body);
      doc.text(row.label, MARGIN, y + 3);
      const barX = MARGIN + 40;
      const barW = CONTENT_W - 80;
      doc.setFillColor(...BRAND.separator);
      doc.rect(barX, y, barW, 4, "F");
      fc(doc, row.color);
      doc.rect(barX, y, (barW * row.pct) / 100, 4, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(`${row.count}`, PAGE_W - MARGIN - 16, y + 3, { align: "right" });
      doc.text(`${row.pct}%`, PAGE_W - MARGIN, y + 3, { align: "right" });
      y += 9;
    });
    y += 4;
  }

  // Avg uplift
  if (sections.uplift !== false) {
    y = ensureRoom(doc, y, 28, c);
    y = drawHeading(doc, "Average uplift between attempts", BRAND.accent, y);
    if (data.uplift.n === 0 || data.uplift.delta === null) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.muted);
      doc.text("No learner has retaken a sim yet.", MARGIN, y);
      y += 8;
    } else {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(28);
      tc(doc, data.uplift.delta >= 0 ? BRAND.strong : BRAND.needsWork);
      const sign = data.uplift.delta >= 0 ? "+" : "";
      doc.text(`${sign}${data.uplift.delta.toFixed(1)}`, MARGIN, y + 8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...BRAND.bodySoft);
      doc.text(`points / 10  ·  across ${data.uplift.n} retake${data.uplift.n === 1 ? "" : "s"}`, MARGIN + 30, y + 8);
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.muted);
      doc.text(
        `Score moved ${data.uplift.firstScore?.toFixed(1)} → ${data.uplift.latestScore?.toFixed(1)}`,
        MARGIN, y + 16,
      );
      y += 22;
    }
  }

  // AI themes
  if (sections.themes !== false && data.themes.length > 0) {
    y = ensureRoom(doc, y, 18, c);
    y = drawHeading(doc, "Cross-cohort AI themes", BRAND.accent, y);
    data.themes.forEach((t) => {
      const themeColor = t.severity === "strong" ? BRAND.strong : t.severity === "developing" ? BRAND.developing : BRAND.needsWork;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(sanitizeForPdf(t.title), MARGIN + 4, y + 3);
      fc(doc, themeColor);
      doc.rect(MARGIN, y - 1, 1.5, 6, "F");
      y += 6;
      y = drawPaginatedTextPanel(doc, MARGIN, y, CONTENT_W, sanitizeForPdf(t.body), c, {
        fontStyle: "normal", fontSize: 10, lineH: 4.6,
        color: BRAND.bodySoft, bg: BRAND.cardBg, border: BRAND.cardBorderSoft,
        padX: 4, padY: 3,
      });
      y += 4;
    });
  }

  // Competency profile
  if (sections.competencies !== false && data.competencies.length > 0) {
    y = ensureRoom(doc, y, 18, c);
    y = drawHeading(doc, "Competency profile", BRAND.accent, y);
    data.competencies.forEach((cmp) => {
      y = ensureRoom(doc, y, 9, c);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.body);
      doc.text(sanitizeForPdf(cmp.name), MARGIN, y + 3);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...BRAND.muted);
      doc.text(`n=${cmp.n}`, MARGIN + 60, y + 3);
      const barX = MARGIN + 75;
      const barW = CONTENT_W - 115;
      doc.setFillColor(...BRAND.separator);
      doc.rect(barX, y, barW, 4, "F");
      const tone = cmp.avgScore !== null
        ? (cmp.avgScore >= 7 ? BRAND.strong : cmp.avgScore >= 5 ? BRAND.developing : BRAND.needsWork)
        : BRAND.muted;
      fc(doc, tone);
      doc.rect(barX, y, (barW * (cmp.avgScore ?? 0)) / 10, 4, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(cmp.avgScore !== null ? cmp.avgScore.toFixed(1) : "—", PAGE_W - MARGIN, y + 3, { align: "right" });
      y += 8;
    });
    y += 4;
  }

  // Top performers
  if (sections.topPerformers !== false && data.topPerformers.length > 0) {
    y = ensureRoom(doc, y, 18, c);
    y = drawHeading(doc, "Top performers", BRAND.accent, y);
    data.topPerformers.forEach((p) => {
      y = ensureRoom(doc, y, 7, c);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.muted);
      doc.text(String(p.rank).padStart(2, "0"), MARGIN, y);
      doc.setTextColor(...BRAND.body);
      doc.text(sanitizeForPdf(p.fullName), MARGIN + 10, y);
      doc.setTextColor(...BRAND.muted);
      doc.text(sanitizeForPdf(p.cohortName), MARGIN + 70, y);
      doc.text(`${p.n} sim${p.n === 1 ? "" : "s"}`, MARGIN + 120, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND.accent);
      doc.text(p.avgScore !== null ? p.avgScore.toFixed(1) : "—", PAGE_W - MARGIN, y, { align: "right" });
      y += 6;
    });
    y += 4;
  }

  // Standout / concern
  if (sections.highlights !== false && (data.standoutCohort !== null || data.concernCohort !== null)) {
    y = ensureRoom(doc, y, 18, c);
    y = drawHeading(doc, "Cohort highlights", BRAND.accent, y);
    if (data.standoutCohort !== null) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.strongText);
      doc.text(spaced("STANDOUT"), MARGIN, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(sanitizeForPdf(data.standoutCohort.cohortName), MARGIN, y + 6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.muted);
      doc.text(`n=${data.standoutCohort.n}  ·  avg ${data.standoutCohort.avgScore?.toFixed(1) ?? "—"} / 10`, MARGIN, y + 12);
      y += 18;
    }
    if (data.concernCohort !== null && data.concernCohort.cohortName !== data.standoutCohort?.cohortName) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.needsText);
      doc.text(spaced("CONCERN"), MARGIN, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(sanitizeForPdf(data.concernCohort.cohortName), MARGIN, y + 6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.muted);
      doc.text(`n=${data.concernCohort.n}  ·  avg ${data.concernCohort.avgScore?.toFixed(1) ?? "—"} / 10`, MARGIN, y + 12);
      y += 18;
    }
  }

  drawFooters(doc);
  return doc.output("blob");
}

// ─── Manager PDF ──────────────────────────────────────────────────────────────
export interface ManagerDashboardPdfData {
  clientName: string;
  period: string;
  funnel: { assigned: number; started: number; inProgress: number; completed: number; notStarted: number };
  actionQueues: {
    notStarted: { total: number; rows: Array<{ fullName: string; cohortName: string; assignedAt: string }> };
    stuck:      { total: number; rows: Array<{ fullName: string; cohortName: string; idleDays: number }> };
    atRisk:     { total: number; rows: Array<{ fullName: string; cohortName: string; avgScore: number | null; simCount: number }> };
  };
  completionBySim: Array<{ simTitle: string; assigned: number; completed: number; completionPct: number }>;
  timeToComplete: Array<{ simTitle: string; medianMinutes: number | null; n: number }>;
  dropOffMap: Array<{ simTitle: string; maxSequence: number | null; reached: Array<{ seq: number; n: number }> }>;
}

export async function generateManagerDashboardPdf(
  data: ManagerDashboardPdfData,
  sections: DashboardPdfSections,
): Promise<Blob> {
  const { jsPDF: JsPDFCtor } = await import("jspdf");
  const doc = new JsPDFCtor({ unit: "mm", format: "a4" });
  const logoWhite = await getLogoWhite();
  const c: Chrome = { docType: "Manager Dashboard", sideRule: BRAND.accent2, logoWhite };

  let y = await drawDashboardCover(doc, c, {
    clientName: data.clientName, view: "Manager", period: data.period,
  });

  // Funnel
  if (sections.funnel !== false) {
    y = ensureRoom(doc, y, 30, c);
    y = drawHeading(doc, "Learner funnel", BRAND.accent2, y);
    const assigned = data.funnel.assigned;
    const conv = (n: number): number => assigned > 0 ? Math.round((n / assigned) * 100) : 0;
    const funnelRows = [
      { label: "Assigned",     count: data.funnel.assigned,    pct: 100,                            color: BRAND.bodySoft },
      { label: "Started",      count: data.funnel.started,     pct: conv(data.funnel.started),      color: BRAND.strong },
      { label: "In progress",  count: data.funnel.inProgress,  pct: conv(data.funnel.inProgress),   color: BRAND.developing },
      { label: "Completed",    count: data.funnel.completed,   pct: conv(data.funnel.completed),    color: BRAND.strong },
      { label: "Not started",  count: data.funnel.notStarted,  pct: conv(data.funnel.notStarted),   color: BRAND.needsWork },
    ] as const;
    funnelRows.forEach((row) => {
      y = ensureRoom(doc, y, 10, c);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.body);
      doc.text(row.label, MARGIN, y + 3);
      const barX = MARGIN + 40;
      const barW = CONTENT_W - 80;
      doc.setFillColor(...BRAND.separator);
      doc.rect(barX, y, barW, 4, "F");
      fc(doc, row.color);
      doc.rect(barX, y, (barW * row.pct) / 100, 4, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(`${row.count}`, PAGE_W - MARGIN - 16, y + 3, { align: "right" });
      doc.text(`${row.pct}%`, PAGE_W - MARGIN, y + 3, { align: "right" });
      y += 9;
    });
    y += 4;
  }

  // Action queues
  if (sections.actionQueues !== false) {
    y = ensureRoom(doc, y, 18, c);
    y = drawHeading(doc, "Action queues", BRAND.accent2, y);

    const queues = [
      { label: "Not started", total: data.actionQueues.notStarted.total, color: BRAND.needsText,
        rows: data.actionQueues.notStarted.rows.map((r) => `${r.fullName}  ·  ${r.cohortName}  ·  assigned ${fmtShortDate(r.assignedAt)}`) },
      { label: "Stuck (7+ days idle)", total: data.actionQueues.stuck.total, color: BRAND.developingText,
        rows: data.actionQueues.stuck.rows.map((r) => `${r.fullName}  ·  ${r.cohortName}  ·  idle ${r.idleDays}d`) },
      { label: "At risk · needs work", total: data.actionQueues.atRisk.total, color: BRAND.needsText,
        rows: data.actionQueues.atRisk.rows.map((r) => `${r.fullName}  ·  ${r.cohortName}  ·  avg ${r.avgScore?.toFixed(1) ?? "—"}  ·  ${r.simCount} sim${r.simCount === 1 ? "" : "s"}`) },
    ];
    queues.forEach((q) => {
      y = ensureRoom(doc, y, 14, c);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      tc(doc, q.color);
      doc.text(`${spaced(q.label.toUpperCase())}  ·  ${q.total}`, MARGIN, y);
      y += 5;
      if (q.rows.length === 0) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.muted);
        doc.text("None.", MARGIN, y);
        y += 6;
      } else {
        q.rows.forEach((line) => {
          y = ensureRoom(doc, y, 5, c);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          doc.setTextColor(...BRAND.bodySoft);
          doc.text(sanitizeForPdf(line), MARGIN, y);
          y += 4.5;
        });
        if (q.total > q.rows.length) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(9);
          doc.setTextColor(...BRAND.muted);
          doc.text(`(+${q.total - q.rows.length} more — see the live dashboard)`, MARGIN, y);
          y += 5;
        }
      }
      y += 4;
    });
  }

  // Completion by sim
  if (sections.completionBySim !== false && data.completionBySim.length > 0) {
    y = ensureRoom(doc, y, 18, c);
    y = drawHeading(doc, "Completion by sim", BRAND.accent2, y);
    data.completionBySim.forEach((s) => {
      y = ensureRoom(doc, y, 9, c);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.body);
      doc.text(sanitizeForPdf(s.simTitle), MARGIN, y + 3);
      const barX = MARGIN + 70;
      const barW = CONTENT_W - 110;
      doc.setFillColor(...BRAND.separator);
      doc.rect(barX, y, barW, 4, "F");
      const tone = s.completionPct >= 70 ? BRAND.strong : s.completionPct >= 50 ? BRAND.developing : BRAND.needsWork;
      fc(doc, tone);
      doc.rect(barX, y, (barW * s.completionPct) / 100, 4, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(`${s.completed}/${s.assigned}`, PAGE_W - MARGIN - 16, y + 3, { align: "right" });
      doc.text(`${s.completionPct}%`, PAGE_W - MARGIN, y + 3, { align: "right" });
      y += 8;
    });
    y += 4;
  }

  // Time-to-complete
  if (sections.timeToComplete !== false && data.timeToComplete.length > 0) {
    y = ensureRoom(doc, y, 18, c);
    y = drawHeading(doc, "Median time-to-complete", BRAND.accent2, y);
    data.timeToComplete.forEach((t) => {
      y = ensureRoom(doc, y, 6, c);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.body);
      doc.text(sanitizeForPdf(t.simTitle), MARGIN, y);
      doc.setTextColor(...BRAND.muted);
      doc.text(`n=${t.n}`, MARGIN + 90, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND.bandDark);
      doc.text(t.medianMinutes !== null ? `${Math.round(t.medianMinutes)} min` : "—", PAGE_W - MARGIN, y, { align: "right" });
      y += 6;
    });
    y += 4;
  }

  // Drop-off — tabular (per-sim summary; live dashboard shows the spark)
  if (sections.dropOff !== false && data.dropOffMap.length > 0) {
    y = ensureRoom(doc, y, 18, c);
    y = drawHeading(doc, "Drop-off (where learners give up)", BRAND.accent2, y);
    data.dropOffMap.forEach((s) => {
      if (s.reached.length === 0 || s.maxSequence === null) return;
      y = ensureRoom(doc, y, 14, c);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.bandDark);
      doc.text(sanitizeForPdf(s.simTitle), MARGIN, y);
      // Find biggest drop
      let biggest = 0;
      let cliff: number | null = null;
      for (let i = 1; i < s.reached.length; i++) {
        const prev = s.reached[i - 1]!.n;
        const cur = s.reached[i]!.n;
        const drop = prev > 0 ? (prev - cur) / prev : 0;
        if (drop > biggest) { biggest = drop; cliff = s.reached[i]!.seq; }
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...BRAND.muted);
      const head = s.reached[0]?.n ?? 0;
      const tail = s.reached[s.reached.length - 1]?.n ?? 0;
      const summary = cliff !== null && biggest > 0.08
        ? `D1 → D${s.maxSequence}: ${head} → ${tail} learners. Biggest cliff at D${cliff} (-${Math.round(biggest * 100)}%).`
        : `D1 → D${s.maxSequence}: ${head} → ${tail} learners. No sharp drop-off.`;
      doc.text(sanitizeForPdf(summary), MARGIN + 6, y + 5);
      y += 11;
    });
  }

  drawFooters(doc);
  return doc.output("blob");
}
